import type { TokenApiDAO } from "@/domains/tokens/types"
import { NativeTokenStore, TokenStore } from "@/domains/tokens/token-store"

import { LcAddress } from "../core"

// Helper function to create TokenStore from TokenApiDAO data
function createStaticToken(data: TokenApiDAO): TokenStore {
  return new TokenStore(data, "system", "static-tokens")
}

const AERO = createStaticToken({
  address: new LcAddress("0x940181a94A35A4569E4529A3CDfB74e38FD98631"),
  symbol: "AERO",
  name: "AERO",
  decimals: 18,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: [],
})

const DEGEN = createStaticToken({
  address: new LcAddress("0x4ed4E862860beD51a9570b96d89aF5E1B0Efefed"),
  symbol: "DEGEN",
  name: "DEGEN",
  decimals: 18,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: [],
})

const EURC = createStaticToken({
  address: new LcAddress("0x60a3E35Cc302bFA44Cb288Bc5a4F316Fdb1adb42"),
  symbol: "EURC",
  name: "EURC",
  decimals: 6,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: [],
})

const GURU = createStaticToken({
  address: new LcAddress("0x0f1cFD0Bb452DB90a3bFC0848349463010419AB2"),
  symbol: "GURU",
  name: "GURU",
  decimals: 18,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: [],
})

const MORPHO = createStaticToken({
  address: new LcAddress("0xBAa5CC21fd487B8Fcc2F632f3F4E8D37262a0842"),
  symbol: "MORPHO",
  name: "MORPHO",
  decimals: 18,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: [],
})

export const SCALE = createStaticToken({
  address: new LcAddress("0x54016a4848a38f257B6E96331F7404073Fd9c32C"),
  symbol: "SCALE",
  name: "SCALE",
  decimals: 18,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: ["blue-chip"],
})

/**
 * Vested token (veSCALE) - represents locked SCALE tokens
 * NOTE: This token is NOT added to the repository as it cannot be swapped
 * It's a reference token for display and calculations only
 */
export const VESTED = createStaticToken({
  address: new LcAddress("0x28c9c71c776a1203000b56c0cca48bef1cd51c53"),
  symbol: "veSCALE",
  name: "veSCALE",
  decimals: 18,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: [],
})

export const USDbC = createStaticToken({
  address: new LcAddress("0xd9aAEc86B65D86f6A7B5B1b0c42FFA531710b6CA"),
  symbol: "USDbC",
  name: "USDbC",
  decimals: 6,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: [],
})

export const USDC = createStaticToken({
  address: new LcAddress("0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"),
  symbol: "USDC",
  name: "USDC",
  decimals: 6,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: ["blue-chip"],
})

export const frxUSD = createStaticToken({
  address: new LcAddress("0xe5020A6d073a794B6E7f05678707dE47986Fb0b6"),
  symbol: "frxUSD",
  name: "Frax USD",
  decimals: 18,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: ["blue-chip"],
})

export const WETH = createStaticToken({
  address: new LcAddress("0x4200000000000000000000000000000000000006"),
  symbol: "WETH",
  name: "WETH",
  decimals: 18,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: [],
})

// ETH delegates price to WETH so the native token always shows WETH's USD price
export const ETH = new NativeTokenStore(
  {
    address: new LcAddress("0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee"),
    symbol: "ETH",
    name: "ETH",
    decimals: 18,
    priceUsd: "0",
    isWhitelisted: true,
    isSwapToken: true,
    isIncentiveToken: false,
    tags: ["blue-chip", "native"],
  },
  "system",
  "static-tokens",
  WETH,
)

const ZRO = createStaticToken({
  address: new LcAddress("0x6985884C4392D348587B19cb9eAAf157F13271cd"),
  symbol: "ZRO",
  name: "ZRO",
  decimals: 18,
  priceUsd: "0",
  isWhitelisted: false,
  isSwapToken: false,
  isIncentiveToken: false,
  tags: [],
})

export const FAME = createStaticToken({
  address: new LcAddress("0xf307e242bfe1ec1ff01a4cef2fdaa81b10a52418"),
  symbol: "FAME",
  name: "FAME",
  decimals: 18,
  priceUsd: "0",
  isWhitelisted: true,
  isSwapToken: true,
  isIncentiveToken: true,
  tags: ["blue-chip"],
})

// ETH is NOT included in staticTokens because it's not an ERC20 token
// It's kept as a static export for direct use, but not loaded into the repository
// Native token balance is handled separately via useNativeBalance hook
export const staticTokens: Array<TokenStore> = [AERO, DEGEN, EURC, FAME, GURU, MORPHO, SCALE, USDbC, USDC, frxUSD, WETH, ZRO]
